/**
 * Ecosystem Page — Agent dependency graph + MCP tools inventory.
 * Graph edges come from volcano invoke_agent / MCP steps, not depends_on alone.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Bot,
  Activity,
  Network,
  Sparkles,
  Loader2,
  Search,
  RefreshCw,
  Zap,
  Wrench,
  Server,
  CheckCircle2,
  XCircle,
  Blocks,
} from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';
import {
  buildIntertwineGraph,
  emptyIntertwine,
  type IntertwineEdge,
  type IntertwineNode,
} from '../agentIntertwine';
import { IntertwinePanel } from '../components/IntertwinePanel';

type Section = 'graph' | 'tools';

function layoutIntertwine(nodes: IntertwineNode[], edges: IntertwineEdge[]) {
  const g = new (dagre as any).graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 48, ranksep: 80, marginx: 24, marginy: 24 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((node) => {
    g.setNode(node.id, { width: node.kind === 'tool' ? 160 : 190, height: 72 });
  });
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  (dagre as any).layout(g);
  const pos = new Map<string, { x: number; y: number }>();
  g.nodes().forEach((id: string) => {
    const laid = g.node(id);
    if (laid) pos.set(id, { x: laid.x, y: laid.y });
  });
  return pos;
}

export default function EcosystemPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState<Section>('graph');
  const selectedId = searchParams.get('agent') || searchParams.get('node') || '';

  const [agents, setAgents] = useState<any[]>([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [graphModel, setGraphModel] = useState(() => buildIntertwineGraph([]));

  const [mcpServers, setMcpServers] = useState<any[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [toolSearch, setToolSearch] = useState('');

  const activeTab = 'ecosystem';

  useEffect(() => {
    loadGraphData();
  }, []);

  useEffect(() => {
    if (section === 'tools') loadMCPTools();
  }, [section]);

  const loadGraphData = async () => {
    setGraphLoading(true);
    setGraphError(null);
    try {
      const res = await cpFetch('/api/agents/discover');
      const list = Array.isArray(res?.agents) ? res.agents : [];
      setAgents(list);
      applyGraph(list, selectedId);
      if (!list.length) {
        setGraphError('Discover returned no agents. Showing 0 agents, not a hidden roster.');
      }
    } catch (err: any) {
      setGraphError(err?.message || 'Could not load agents for the dependency graph.');
    } finally {
      setGraphLoading(false);
    }
  };

  const applyGraph = (agentList: any[], highlightId = '') => {
    const model = buildIntertwineGraph(agentList);
    setGraphModel(model);
    const positions = layoutIntertwine(model.nodes, model.edges);
    const neighborIds = new Set<string>();
    if (highlightId) {
      neighborIds.add(highlightId);
      for (const edge of model.edges) {
        if (edge.source === highlightId || edge.target === highlightId) {
          neighborIds.add(edge.source);
          neighborIds.add(edge.target);
        }
      }
    }

    const flowNodes = model.nodes.map((node) => {
      const isTool = node.kind === 'tool';
      const selected = highlightId === node.id;
      const dimmed = Boolean(highlightId) && !neighborIds.has(node.id);
      const pos = positions.get(node.id) || { x: 40, y: 40 };
      return {
        id: node.id,
        position: { x: pos.x, y: pos.y },
        data: {
          kind: node.kind,
          label: (
            <div className="text-center px-2 py-1">
              <div className="flex items-center justify-center gap-1 mb-1">
                {isTool ? <Wrench className="w-3 h-3 text-sky-600" /> : <Zap className="w-3 h-3 text-amber-500" />}
                <span className="text-xs font-semibold truncate max-w-[130px]">{node.label}</span>
              </div>
              <div className="text-[10px] text-gray-500">
                {isTool ? (node.mcp_server || 'MCP tool') : (node.stub ? 'stub — not production' : 'agent')}
              </div>
            </div>
          ),
        },
        style: {
          background: isTool ? '#F0F9FF' : node.stub ? '#FFF7ED' : selected ? '#EDE9FE' : '#FFFBEB',
          border: `1.5px solid ${isTool ? '#0EA5E9' : node.stub ? '#F97316' : selected ? '#7C3AED' : '#F59E0B'}`,
          borderRadius: '10px',
          padding: '4px',
          minWidth: isTool ? '140px' : '160px',
          opacity: dimmed ? 0.28 : 1,
        },
      };
    });

    const flowEdges = model.edges.map((edge) => {
      const related = !highlightId || edge.source === highlightId || edge.target === highlightId;
      const agentEdge = edge.kind === 'invokes_agent';
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        animated: related,
        label: edge.label,
        labelStyle: { fontSize: 9, fill: '#6B7280' },
        style: {
          stroke: agentEdge ? '#7C3AED' : '#0EA5E9',
          strokeWidth: related ? 1.6 : 1,
          strokeDasharray: agentEdge ? undefined : '4 2',
          opacity: related ? 1 : 0.2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: agentEdge ? '#7C3AED' : '#0EA5E9',
          width: 12,
          height: 12,
        },
      };
    });

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  useEffect(() => {
    if (agents.length) applyGraph(agents, selectedId);
  }, [selectedId]);

  const loadMCPTools = async () => {
    setToolsLoading(true);
    setToolsError(null);
    try {
      const res = await cpFetch('/cp/api/volcano/mcp/servers');
      const servers = Array.isArray(res?.servers) ? res.servers : res?.discovery?.servers || [];
      if (!servers.length) {
        setMcpServers([]);
        setToolsError('Volcano MCP server list returned empty. Overview 24/96 is a different store — this is not a measured zero.');
        return;
      }
      const enriched = await Promise.all(
        servers.map(async (s: any) => {
          try {
            const toolRes = await cpFetch(`/cp/api/volcano/mcp/${s.name || s.serverName}/tools`);
            return { ...s, tools: toolRes?.tools || [], tools_unavailable: false };
          } catch {
            return { ...s, tools: [], tools_unavailable: true };
          }
        })
      );
      setMcpServers(enriched);
      if (enriched.every((s) => s.tools_unavailable)) {
        setToolsError('MCP tool lists were unavailable. Showing unknown, not 0 tools.');
      }
    } catch {
      setMcpServers([]);
      setToolsError('Could not load Volcano MCP servers. Tool count is unknown, not 0.');
    } finally {
      setToolsLoading(false);
    }
  };

  const filteredServers = useMemo(() => {
    if (!toolSearch) return mcpServers;
    const q = toolSearch.toLowerCase();
    return mcpServers.filter((s) => {
      const name = (s.name || s.serverName || '').toLowerCase();
      const toolNames = (s.tools || []).map((t: any) => (t.name || '').toLowerCase()).join(' ');
      return name.includes(q) || toolNames.includes(q);
    });
  }, [mcpServers, toolSearch]);

  const toolsMeasured = !toolsError && mcpServers.some((srv) => !srv.tools_unavailable);
  const inventoryTools = toolsMeasured
    ? mcpServers.reduce((s, srv) => s + (srv.tools?.length || 0), 0)
    : null;
  const agentCount = graphModel.nodes.filter((node) => node.kind === 'agent').length;
  const graphToolCount = graphModel.nodes.filter((node) => node.kind === 'tool').length;
  const graphEdgeCount = graphModel.edges.length;

  const agentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents) {
      const id = agent.agent_id || agent.id;
      if (id) map.set(id, agent.name || id);
    }
    return map;
  }, [agents]);

  const selectedIntertwine = selectedId
    ? graphModel.byAgent.get(selectedId) || emptyIntertwine(selectedId)
    : null;
  const selectedIsTool = selectedId.startsWith('tool:');
  const selectedToolUpstream = selectedIsTool
    ? graphModel.edges.filter((edge) => edge.target === selectedId).map((edge) => edge.source)
    : [];

  const onNodeClick = useCallback((_: any, node: any) => {
    const next = new URLSearchParams(searchParams);
    if (node.id.startsWith('tool:')) {
      next.set('node', node.id);
      next.delete('agent');
    } else {
      next.set('agent', node.id);
      next.delete('node');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="h-full flex flex-col">
      <div className="border-b bg-white px-6 pt-4 pb-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Agent Manager</h1>
            <p className="text-xs text-gray-500">Build, run, and observe your agentic workflows</p>
          </div>
        </div>

        <nav className="flex gap-1 -mb-px">
          {[
            { id: 'hub', label: 'Agents', icon: Bot, path: '/agents/hub' },
            { id: 'create', label: 'Create', icon: Sparkles, path: '/agents/create' },
            { id: 'observe', label: 'Observe', icon: Activity, path: '/agents/observe' },
            { id: 'ecosystem', label: 'Ecosystem', icon: Network, path: '/agents/ecosystem' },
            { id: 'steps', label: 'Steps', icon: Blocks, path: '/agents/steps' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="flex items-center gap-2 px-6 pt-4 pb-2">
          {[
            { id: 'graph' as Section, label: 'Dependency Graph', icon: Network },
            { id: 'tools' as Section, label: 'MCP Tools Map', icon: Wrench },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                section === s.id
                  ? 'bg-violet-100 text-violet-700'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              <s.icon className="w-4 h-4" />
              {s.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <Badge variant="secondary" className="text-xs">{agentCount} agents</Badge>
            {section === 'graph' ? (
              <>
                <Badge variant="secondary" className="text-xs">{graphToolCount} tools from workflows</Badge>
                <Badge variant="secondary" className="text-xs">{graphEdgeCount} edges</Badge>
              </>
            ) : (
              <Badge variant="secondary" className="text-xs">
                {inventoryTools == null ? 'tools unknown' : `${inventoryTools} tools`}
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={loadGraphData} disabled={graphLoading}>
              <RefreshCw className={`w-4 h-4 ${graphLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {section === 'graph' && (
          <div className="px-6 pb-6 flex gap-4" style={{ height: 'calc(100% - 60px)' }}>
            <div className="flex-1 min-w-0 border rounded-xl bg-white overflow-hidden">
              {graphLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">Building dependency graph...</span>
                </div>
              ) : (
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  fitView
                  fitViewOptions={{ padding: 0.3 }}
                  minZoom={0.2}
                  maxZoom={2}
                >
                  <Background color="#E5E7EB" gap={20} />
                  <Controls position="bottom-right" />
                  <MiniMap
                    nodeColor={(n) => String(n.id).startsWith('tool:') ? '#E0F2FE' : '#FEF3C7'}
                    maskColor="rgba(255,255,255,0.8)"
                    style={{ border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                </ReactFlow>
              )}
            </div>
            <div className="w-80 shrink-0 border rounded-xl bg-white p-4 overflow-auto">
              {graphError && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  {graphError}
                </div>
              )}
              {!selectedId && (
                <p className="text-sm text-gray-500">
                  Click an agent to see who it invokes and who invokes it. Tool nodes are shared when two agents call the same MCP tool.
                </p>
              )}
              {selectedIsTool && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-sky-600" />
                    <h2 className="text-sm font-semibold text-gray-900 break-all">{selectedId.replace(/^tool:/, '')}</h2>
                  </div>
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Agents that invoke this tool</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedToolUpstream.length === 0 ? (
                      <span className="text-xs text-gray-400">None</span>
                    ) : (
                      selectedToolUpstream.map((id) => (
                        <Badge
                          key={id}
                          variant="outline"
                          className="text-xs cursor-pointer hover:bg-violet-50"
                          onClick={() => setSearchParams({ agent: id }, { replace: true })}
                        >
                          <Bot className="w-3 h-3 mr-1" />
                          {agentNameMap.get(id) || id}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
              )}
              {selectedIntertwine && !selectedIsTool && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      {agentNameMap.get(selectedId) || selectedId}
                    </h2>
                    <p className="text-xs text-gray-500 font-mono break-all">{selectedId}</p>
                  </div>
                  <IntertwinePanel
                    intertwine={selectedIntertwine}
                    agentNameMap={agentNameMap}
                    onOpenAgent={(id) => setSearchParams({ agent: id }, { replace: true })}
                    compact
                  />
                  <Button size="sm" variant="outline" onClick={() => navigate(`/agents/hub/${selectedId}`)}>
                    Open agent
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {section === 'tools' && (
          <div className="px-6 pb-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Search servers or tools..." value={toolSearch} onChange={(e) => setToolSearch(e.target.value)} className="pl-9 bg-white" />
              </div>
              <Button variant="ghost" size="sm" onClick={loadMCPTools} disabled={toolsLoading}>
                <RefreshCw className={`w-4 h-4 ${toolsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {toolsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Loading MCP servers...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredServers.map((srv: any) => {
                  const name = srv.name || srv.serverName || 'unknown';
                  const tools = srv.tools || [];
                  const healthy = srv.status === 'connected' || srv.healthy !== false;
                  return (
                    <Card key={name} className="bg-white">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-violet-600" />
                            <CardTitle className="text-sm">{name}</CardTitle>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {healthy
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                              : <XCircle className="w-3.5 h-3.5 text-red-500" />
                            }
                            <Badge variant="secondary" className="text-[10px]">{tools.length} tools</Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {srv.tools_unavailable ? (
                          <p className="text-xs text-amber-700">Tool list unavailable — not 0</p>
                        ) : tools.length === 0 ? (
                          <p className="text-xs text-gray-400">No tools returned for this server</p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {tools.slice(0, 12).map((tool: any) => (
                              <Badge key={tool.name} variant="outline" className="text-[10px] font-mono">
                                {tool.name}
                              </Badge>
                            ))}
                            {tools.length > 12 && (
                              <Badge variant="secondary" className="text-[10px]">+{tools.length - 12} more</Badge>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                {toolsError && (
                  <div className="col-span-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {toolsError}
                  </div>
                )}
                {filteredServers.length === 0 && !toolsError && (
                  <div className="col-span-full py-12 text-center text-gray-500">
                    <Server className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium">No MCP servers found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
